import React from 'react'
import type { Highlights, TopicHighlight, MarketOutlookHighlight, HeldAwayHighlight, ReferralHighlight } from '../store/insights'
import { type TimeRangeKey, TIME_RANGE_LABELS } from '../utils/insightsTransforms'
import { InfoTooltip, MeetingTypeName } from './insightsCommon'

const TopicHighlightCard: React.FC<{
  icon: React.ReactNode
  label: string
  data: TopicHighlight | null
  emptyTitle: string
  emptyDetail: string
  tooltip?: string
}> = ({ icon, label, data, emptyTitle, emptyDetail, tooltip }) => (
  <div className='insights-highlight-item'>
    <div className='insights-highlight-item__header'>
      <div className='insights-highlight-item__icon'>{icon}</div>
      <div className='insights-highlight-item__label insights-label-with-tooltip'>
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
    </div>
    <div className='insights-highlight-item__content'>
      <div className='insights-highlight-item__topic'>{data?.topic || emptyTitle}</div>
      <div className='insights-highlight-item__detail'>{data?.detail || emptyDetail}</div>
    </div>
  </div>
)

const SummaryHighlightCard: React.FC<{
  icon: React.ReactNode
  label: string
  tooltip?: string
  bar?: React.ReactNode
  children: React.ReactNode
}> = ({ icon, label, tooltip, bar, children }) => (
  <div className='insights-summary-card'>
    <div className='insights-summary-card__header'>
      <div className='insights-summary-card__icon'>{icon}</div>
      <div className='insights-summary-card__label insights-label-with-tooltip'>
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
    </div>
    {bar ?? <div className='insights-summary-card__bar-empty' />}
    {children}
  </div>
)

const SummaryContent: React.FC<{ text: string; details: React.ReactNode[] }> = ({ text, details }) => (
  <div className='insights-summary-card__content'>
    <div className='insights-summary-card__text'>{text}</div>
    {details.map((detail, i) => (
      <div key={i} className='insights-summary-card__detail'>
        {detail}
      </div>
    ))}
  </div>
)

const OutlookHighlightCard: React.FC<{ data: MarketOutlookHighlight | null }> = ({ data }) => (
  <SummaryHighlightCard
    icon={<i className='bi bi-binoculars' />}
    label='MARKET OUTLOOK'
    tooltip='Overall client sentiment across all asset classes'
    bar={
      data && (
        <div className='insights-summary-card__bar d-flex'>
          <div className='insights-summary-card__bar-segment--optimistic' style={{ width: `${data.optimisticPercent}%` }} />
          <div className='insights-summary-card__bar-segment--concerned' style={{ width: `${data.concernedPercent}%` }} />
          <div className='insights-summary-card__bar-segment--neutral' style={{ width: `${data.neutralPercent}%` }} />
        </div>
      )
    }
  >
    <SummaryContent
      text={data ? data.summaryText : 'No sentiment detected'}
      details={
        data
          ? [
              data.strongestOpportunity && (
                <>
                  <strong>{data.strongestOpportunity}</strong> is the strongest opportunity
                </>
              ),
              data.topConcern && (
                <>
                  <strong>{data.topConcern}</strong> is top of mind for caution
                </>
              ),
            ].filter(Boolean)
          : ['Try selecting a wider time range']
      }
    />
  </SummaryHighlightCard>
)

const HeldAwayHighlightCard: React.FC<{ data: HeldAwayHighlight | null }> = ({ data }) => (
  <SummaryHighlightCard
    icon={<i className='bi bi-bullseye' />}
    label='HELD-AWAY ASSETS'
    tooltip='Track how held-away assets move from mention to client agreement'
    bar={
      data && (
        <div className='insights-summary-card__bar d-flex'>
          <div className='insights-summary-card__bar-segment--purple' style={{ width: `${data.agreedPercent}%` }} />
          <div className='insights-summary-card__bar-segment--purple-light' style={{ width: `${Math.max(0, data.suggestedPercent - data.agreedPercent)}%` }} />
        </div>
      )
    }
  >
    <SummaryContent
      text={data ? data.summaryText : 'No asset mentions'}
      details={
        data
          ? [
              <>
                <strong>
                  <MeetingTypeName name={data.leadMeetingType} />
                </strong>{' '}
                lead in asset mentions
              </>,
              <>
                <strong>
                  <MeetingTypeName name={data.bestConvertMeetingType} />
                </strong>{' '}
                convert best
              </>,
            ]
          : ['Try selecting a wider time range']
      }
    />
  </SummaryHighlightCard>
)

const ReferralsHighlightCard: React.FC<{ data: ReferralHighlight | null }> = ({ data }) => {
  return (
    <SummaryHighlightCard
      icon={<i className='bi bi-people' />}
      label='REFERRALS'
      bar={
        data && (
          <div className='insights-summary-card__bar d-flex'>
            <div className='insights-summary-card__bar-segment--purple' style={{ width: `${data.commitRate}%` }} />
          </div>
        )
      }
    >
      <SummaryContent
        text={data ? data.summaryText : 'No referral activity'}
        details={
          data
            ? [
                <>
                  <strong>
                    <MeetingTypeName name={data.leadAskMeetingType} />
                  </strong>{' '}
                  lead in referral asks
                </>,
                <>
                  <strong>
                    <MeetingTypeName name={data.leadProvidedMeetingType} />
                  </strong>{' '}
                  lead in referrals provided
                </>,
              ]
            : ['Try selecting a wider time range']
        }
      />
    </SummaryHighlightCard>
  )
}

export const HighlightsSection: React.FC<{ highlights: Highlights; timeRange: TimeRangeKey }> = ({ highlights, timeRange }) => {
  const hasAnyTopicData = highlights.mostDiscussed || highlights.mostClientDriven || highlights.mostTrending || highlights.mostQuestionsRaised
  const hasAnySummaryData = highlights.marketOutlook || highlights.heldAwayAssets || highlights.referrals

  return (
    <div className='insights-highlights-card'>
      <div>
        <h2 className='insights-highlights-card__title'>Highlights of the last {TIME_RANGE_LABELS[timeRange]}</h2>
        <p className='insights-highlights-card__subtitle'>What the conversations are telling you</p>
      </div>

      <div className='insights-highlights-grid'>
        <TopicHighlightCard
          icon={<i className='bi bi-chat-dots' />}
          label='MOST DISCUSSED TOPIC'
          data={highlights.mostDiscussed}
          emptyTitle='No topics found'
          emptyDetail='Try selecting a wider time range'
        />
        <TopicHighlightCard
          icon={<i className='bi bi-person' />}
          label='MOST CLIENT-DRIVEN TOPIC'
          data={highlights.mostClientDriven}
          emptyTitle='No client-driven topics'
          emptyDetail='Try selecting a wider time range'
          tooltip='Topics the client raised themselves, without prompting from the user'
        />
        <TopicHighlightCard
          icon={<i className='bi bi-graph-up' />}
          label='MOST TRENDING TOPIC'
          data={highlights.mostTrending}
          emptyTitle='No comparison data yet'
          emptyDetail='Trend data will appear once you have two full periods captured'
        />
        <TopicHighlightCard
          icon={<i className='bi bi-question-circle' />}
          label='MOST QUESTIONS RAISED'
          data={highlights.mostQuestionsRaised}
          emptyTitle='No client questions'
          emptyDetail='Try selecting a wider time range'
          tooltip='Questions clients asked about this topic during conversations'
        />
      </div>

      {(hasAnySummaryData || !hasAnyTopicData) && (
        <>
          <div className='insights-highlights-separator' />
          <div className='insights-summary-grid'>
            <OutlookHighlightCard data={highlights.marketOutlook} />
            <div className='insights-summary-separator' />
            <HeldAwayHighlightCard data={highlights.heldAwayAssets} />
            <div className='insights-summary-separator' />
            <ReferralsHighlightCard data={highlights.referrals} />
          </div>
        </>
      )}
    </div>
  )
}
